import { Link } from 'react-router-dom';
import { itemLabel } from '../../components/ItemName';
import { usePartsStatus, PartsStatusRow } from '../usePartsStatus';
import Panel from '../../components/ui/Panel';
import Alert from '../../components/ui/Alert';
import EmptyState from '../../components/ui/EmptyState';
import { Progress } from '../../components/ui/Progress';
import { Skeleton, SkeletonGroup } from '../../components/ui/Skeleton';
import Tag from '../../components/ui/Tag';
import { CATEGORY } from '../../components/ui/categoryColors';

/**
 * Low Stock Alert — Phase 1b rewrite.
 *
 * Data source: GET /api/dashboard/parts-status. Shows the rows the server
 * marks `is_short`, which is membership in the reorder report: buildable on
 * hand plus what is on order, at or below the threshold. This card never
 * judges "short" itself. See web/docs/features/reorder-levels.md.
 *
 * The bar's fill ratio compares on hand plus on order to the threshold.
 */
const LowPartsModule: React.FC = () => {
  const { data, loading, error } = usePartsStatus();

  if (loading) {
    return (
      <Panel label="Low stock alert">
        <SkeletonGroup label="Loading parts status" className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </SkeletonGroup>
      </Panel>
    );
  }
  if (error) {
    return (
      <Panel label="Low stock alert">
        <Alert variant="note" tone="error">{error}</Alert>
      </Panel>
    );
  }

  // Largest shortfall first, the reorder report's own order.
  const shortfall = (p: PartsStatusRow) => (p.reorder_threshold ?? 0) - (p.quantity_on_hand + p.on_order);
  const lowParts: PartsStatusRow[] = (data?.rows || [])
    .filter((p) => p.is_short)
    .sort((a, b) => shortfall(b) - shortfall(a));

  if (lowParts.length === 0) {
    return (
      <Panel label="Low stock alert">
        <EmptyState variant="area">All parts above reorder threshold</EmptyState>
      </Panel>
    );
  }

  return (
    <Panel
      title="Low Stock Alert"
      titleStyle="section"
      accent="red"
      lit
      level={3}
      actions={(
        <Tag size="sm" hex={CATEGORY.red.ink.hex} role="status" aria-live="polite">
          {lowParts.length} {lowParts.length === 1 ? 'part' : 'parts'}
        </Tag>
      )}
    >
      <div className="space-y-2">
        {lowParts.map((part) => {
          const qty = part.quantity_on_hand;
          const onOrder = part.on_order;
          const threshold = part.reorder_threshold!;
          const covered = qty + onOrder;
          // At or below half of threshold is critical: red and the
          // word "Critical". Ratio-based instead of the legacy redstock field.
          const isCritical = qty <= Math.floor(threshold / 2);
          const tone = isCritical ? CATEGORY.red : CATEGORY.orange;

          return (
            <Link
              key={part.id}
              to={`/parts-catalog/${part.id}`}
              className="flex items-center gap-3 group hover:bg-gray-700/20 rounded-md px-2 py-1.5 -mx-2 transition-colors"
              aria-label={`Open ${itemLabel(part)}, ${qty} on hand${onOrder ? `, ${onOrder} on order` : ''} vs threshold ${threshold}`}
            >
              {/* The NAME, not the code. A dashboard is read at a glance from
                  across a room, and "Gen2 Pyro" reads at that distance in a way
                  FF-PYR-02-1-CH never did. The SKU is on the item's own page,
                  one click away. The title carries the full name when the column
                  truncates it. */}
              <span
                className="text-xs font-medium w-32 truncate"
                style={{ color: tone.ink.oklch }}
                title={itemLabel(part)}
              >
                {itemLabel(part)}
              </span>
              <Progress
                className="flex-1"
                value={covered}
                max={threshold}
                label={`${itemLabel(part)} stock against reorder threshold`}
                size="md"
                tone="primary"
                color={tone.ink.hex}
              />
              <span
                className="text-xs font-semibold tabular-nums w-24 text-right"
                style={{ color: tone.ink.oklch }}
              >
                {isCritical && <span className="block text-[10px] font-semibold">Critical</span>}
                {qty} / {threshold}
                {onOrder > 0 && (
                  <span className="block text-[10px] font-normal text-on-surface-variant">+{onOrder} on order</span>
                )}
              </span>
            </Link>
          );
        })}
      </div>
    </Panel>
  );
};

export default LowPartsModule;
